"use client";

import { api } from "@agroproj/backend/convex/_generated/api";
import type { Id } from "@agroproj/backend/convex/_generated/dataModel";
import { useAction, useMutation, useQuery } from "convex/react";
import { Check, CreditCard } from "lucide-react";
import { useParams, usePathname } from "next/navigation";
import { useState } from "react";
import { toast } from "sonner";
import { DetailsItem } from "@/components/details-item";
import { LoadingPage } from "@/components/loading-page";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Section, SectionStack } from "@/components/ui/section";
import { Skeleton } from "@/components/ui/skeleton";
import { StatGrid, StatTile } from "@/components/ui/stat";
import { metaTextClassName, statValueClassName } from "@/components/ui/typography";
import { EMPTY_VALUE, formatCentsBRL } from "@/lib/format";
import { getPlanDisplayName } from "@/lib/pricing-display";
import { matchPriceOptionForIntent, type PlanIntent } from "@/lib/pricing-intent";
import { cn } from "@/lib/utils";
type BillingAction = "checkout" | "free" | "portal";
const statusLabels: Record<string, string> = {
  none: "Sem assinatura",
  incomplete: "Pagamento pendente",
  incomplete_expired: "Checkout expirado",
  trialing: "Em teste",
  active: "Ativa",
  past_due: "Pagamento atrasado",
  canceled: "Cancelada",
  unpaid: "Inadimplente",
  paused: "Pausada"
};

/** Long pt-BR date (`25 de set. de 2026`) for billing periods. */
function formatDate(timestamp?: number) {
  if (!timestamp) return null;
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  }).format(new Date(timestamp));
}

/** Stripe prices carry their own currency; BRL uses the shared formatter. */
function formatPrice(amount: number | undefined, currency: string) {
  if (amount === undefined) return "Sob consulta";
  if (currency.toLowerCase() === "brl") return formatCentsBRL(amount);
  // ui-audit-ignore-next-line format/use-shared-formatters: Stripe prices may be in other currencies
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency.toUpperCase()
  }).format(amount / 100);
}
function intervalLabel(interval: string | undefined, count: number | undefined) {
  if (!interval) return "";
  const quantity = count && count > 1 ? count : 1;
  if (interval === "month") return quantity === 1 ? "/mês" : `/${quantity} meses`;
  if (interval === "year") return quantity === 1 ? "/ano" : `/${quantity} anos`;
  return "";
}
function intervalName(interval: string | undefined) {
  if (interval === "month") return "Mensal";
  if (interval === "year") return "Anual";
  return null;
}
function getMetadataFeatures(priceMetadata: Record<string, string> | undefined, productMetadata: Record<string, string> | undefined) {
  const rawFeatures = priceMetadata?.features ?? productMetadata?.features;
  if (!rawFeatures) return [];
  return rawFeatures.split("|").map(feature => feature.trim()).filter(Boolean);
}
function getPlanBadgeLabel(price: {
  metadata?: Record<string, string>;
  unitAmount?: number;
}) {
  if (price.unitAmount === 0) return "Teste";
  if (price.metadata?.billingModel === "per_project") return "Por projeto";
  if (price.metadata?.billingInterval === "year") return "Anual";
  if (price.metadata?.billingModel === "unlimited") return "Ilimitado";
  return null;
}
type PriceOption = {
  product: {
    name: string;
    metadata?: Record<string, string>;
  };
  price: {
    _id: Id<"subscription_prices">;
    providerPriceId: string;
    metadata?: Record<string, string>;
    unitAmount?: number;
    currency: string;
    recurringInterval?: string;
    recurringIntervalCount?: number;
  };
};
function findActivePriceOption(billing: {
  subscriptionPriceId?: Id<"subscription_prices">;
  providerPriceId?: string;
  stripePriceId?: string;
} | null, priceOptions: PriceOption[]) {
  if (!billing) return null;
  if (billing.subscriptionPriceId) {
    const bySubscriptionPrice = priceOptions.find(({
      price
    }) => price._id === billing.subscriptionPriceId);
    if (bySubscriptionPrice) return bySubscriptionPrice;
  }
  const providerPriceId = billing.providerPriceId ?? billing.stripePriceId;
  if (providerPriceId) {
    return priceOptions.find(({
      price
    }) => price.providerPriceId === providerPriceId) ?? null;
  }
  return null;
}
function getSubscriptionAmountDisplay(price: PriceOption["price"], billableProjectCount: number) {
  const isPerProject = price.metadata?.billingModel === "per_project";
  const quantity = isPerProject ? Math.max(1, billableProjectCount) : 1;
  const unitAmount = price.unitAmount;
  const currency = price.currency;
  const intervalSuffix = intervalLabel(price.recurringInterval, price.recurringIntervalCount);
  if (unitAmount === undefined) {
    return {
      primary: "Sob consulta",
      secondary: intervalSuffix,
      detail: null as string | null
    };
  }
  if (isPerProject && quantity > 1) {
    const total = unitAmount * quantity;
    return {
      primary: formatPrice(total, currency),
      secondary: intervalSuffix,
      detail: `${formatPrice(unitAmount, currency)} × ${quantity} projeto${quantity === 1 ? "" : "s"}`
    };
  }
  return {
    primary: formatPrice(unitAmount, currency),
    secondary: intervalSuffix,
    detail: isPerProject ? `Cobrança por projeto (${quantity} projeto${quantity === 1 ? "" : "s"})` : null
  };
}

/**
 * Workspace subscription UI. `settings` shows the current plan and plan switching;
 * `required` shows the plan chooser used by the billing wall (page and dialog).
 */
export function OrganizationBillingContent({
  mode = "settings",
  initialIntent = null
}: {
  mode?: "settings" | "required";
  initialIntent?: PlanIntent | null;
}) {
  const {
    organizationSlug
  } = useParams<{
    organizationSlug: string;
  }>();
  const pathname = usePathname();
  const billingStatus = useQuery(api.payments.queries.getOrganizationBillingStatus);
  const billingUsage = useQuery(api.payments.queries.getOrganizationBillingUsage);
  const plans = useQuery(api.payments.catalog.listPublicSubscriptionPlans);
  const activateFreeSubscription = useMutation(api.payments.mutations.activateFreeSubscription);
  const createCheckout = useAction(api.payments.actions.createSubscriptionCheckoutSession);
  const createPortal = useAction(api.payments.actions.createBillingPortalSession);
  const [pendingAction, setPendingAction] = useState<BillingAction | null>(null);
  const [pendingPriceId, setPendingPriceId] = useState<Id<"subscription_prices"> | null>(null);
  const [selectedPriceId, setSelectedPriceId] = useState<Id<"subscription_prices"> | null>(null);
  const billing = billingStatus?.billing ?? null;
  const status = billingStatus?.status ?? "none";
  const hasActiveSubscription = billingStatus?.hasActiveSubscription ?? false;
  const periodStart = formatDate(billing?.currentPeriodStart);
  const periodEnd = formatDate(billing?.currentPeriodEnd);
  const trialEnd = formatDate(billing?.trialEnd);
  const hasBillingCustomer = Boolean(billing?.providerCustomerId ?? billing?.stripeCustomerId);
  const canOpenBillingPortal = billing?.provider === "stripe" && hasBillingCustomer;
  const hasInternalSubscription = hasActiveSubscription && billing?.provider === "internal";
  const availablePlans = plans ?? [];
  const priceOptions = availablePlans.flatMap(plan => plan.prices.map(price => ({
    product: plan.product,
    price
  })));
  const firstAvailablePrice = priceOptions[0]?.price;
  const firstUpgradePrice = hasInternalSubscription ? priceOptions.find(({
    price: price_0
  }) => price_0.unitAmount !== 0)?.price ?? firstAvailablePrice : firstAvailablePrice;
  const intentPrice = matchPriceOptionForIntent(priceOptions, initialIntent)?.price;
  const checkoutPriceId = selectedPriceId ?? intentPrice?._id ?? firstUpgradePrice?._id ?? null;
  const checkoutPrice = priceOptions.find(({
    price: price_1
  }) => price_1._id === checkoutPriceId)?.price;
  const isCheckoutFreePlan = checkoutPrice?.unitAmount === 0;
  const billableProjectCount = billingUsage?.billableProjectCount ?? 0;
  const isRequiredPlanChooser = mode === "required" && !hasActiveSubscription;
  const activePriceOption = findActivePriceOption(billing, priceOptions);
  const activePlanName = activePriceOption ? getPlanDisplayName({
    fallbackName: activePriceOption.product.name,
    priceMetadata: activePriceOption.price.metadata,
    productMetadata: activePriceOption.product.metadata,
    recurringInterval: activePriceOption.price.recurringInterval
  }) : null;
  const activePlanBadge = activePriceOption ? getPlanBadgeLabel(activePriceOption.price) : null;
  const activePlanInterval = activePriceOption ? intervalName(activePriceOption.price.recurringInterval) : null;
  const activeAmountDisplay = activePriceOption ? getSubscriptionAmountDisplay(activePriceOption.price, billableProjectCount) : null;
  const showSubscriptionSummary = !isRequiredPlanChooser && Boolean(billing && (hasActiveSubscription || status !== "none"));
  const shouldShowPlanSelector = !isRequiredPlanChooser && (!hasActiveSubscription || hasInternalSubscription) && availablePlans.length > 0;
  const activateFreePlan = async (priceId: Id<"subscription_prices">) => {
    setPendingAction("free");
    setPendingPriceId(priceId);
    try {
      await activateFreeSubscription({
        subscriptionPriceId: priceId
      });
      toast.success("Plano gratuito ativado");
    } catch {
      toast.error("Não foi possível ativar o plano gratuito");
      setPendingAction(null);
      setPendingPriceId(null);
    }
  };
  const startCheckout = async (priceId_0?: Id<"subscription_prices"> | null) => {
    const subscriptionPriceId = priceId_0 ?? checkoutPriceId;
    setPendingAction("checkout");
    setPendingPriceId(subscriptionPriceId);
    try {
      const result = await createCheckout({
        organizationSlug,
        returnPath: pathname,
        subscriptionPriceId: subscriptionPriceId ?? undefined
      });
      window.location.assign(result.url);
    } catch {
      toast.error("Não foi possível iniciar o checkout");
      setPendingAction(null);
      setPendingPriceId(null);
    }
  };
  const openPortal = async () => {
    setPendingAction("portal");
    try {
      const result_0 = await createPortal({
        organizationSlug,
        returnPath: pathname
      });
      window.location.assign(result_0.url);
    } catch {
      toast.error("Não foi possível abrir o portal de cobrança");
      setPendingAction(null);
    }
  };
  if (billingStatus === undefined || isRequiredPlanChooser && plans === undefined) {
    return <LoadingPage />;
  }
  const primaryAction = () => {
    if (canOpenBillingPortal) {
      void openPortal();
      return;
    }
    if (checkoutPriceId && isCheckoutFreePlan) {
      void activateFreePlan(checkoutPriceId);
      return;
    }
    void startCheckout();
  };
  const primaryLabel = canOpenBillingPortal ? "Gerenciar cobrança" : isCheckoutFreePlan ? "Ativar plano gratuito" : "Assinar agora";
  const primaryPendingLabel = canOpenBillingPortal ? "Abrindo portal..." : isCheckoutFreePlan ? "Ativando..." : "Abrindo checkout...";
  const primaryPending = pendingAction === (canOpenBillingPortal ? "portal" : isCheckoutFreePlan ? "free" : "checkout");
  const statusBadge = <Badge variant={hasActiveSubscription ? "status-active" : "destructive"}>
      {statusLabels[status] ?? status}
    </Badge>;
  const billedProjectCount = Math.max(1, billableProjectCount);
  const billedProjectsLabel = `${billedProjectCount} projeto${billedProjectCount === 1 ? "" : "s"}`;
  const requiredPlanChooser = priceOptions.length > 0 ? <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {priceOptions.map(({
      product,
      price: price_2
    }) => {
      const isPerProject = price_2.metadata?.billingModel === "per_project";
      const isFreePlan = price_2.unitAmount === 0;
      const isPending = (pendingAction === "checkout" || pendingAction === "free") && pendingPriceId === price_2._id;
      const features = getMetadataFeatures(price_2.metadata, product.metadata);
      const badgeLabel = getPlanBadgeLabel(price_2);
      const planInterval = intervalName(price_2.recurringInterval);
      const planName = getPlanDisplayName({
        fallbackName: product.name,
        priceMetadata: price_2.metadata,
        productMetadata: product.metadata,
        recurringInterval: price_2.recurringInterval
      });
      return <Card key={price_2._id} size="sm">
              <CardHeader>
                <CardTitle>{planName}</CardTitle>
                {planInterval ? <CardDescription>{planInterval}</CardDescription> : null}
                {badgeLabel ? <CardAction>
                    <Badge variant="outline">{badgeLabel}</Badge>
                  </CardAction> : null}
              </CardHeader>
              <CardContent className="flex flex-1 flex-col gap-3">
                <p>
                  <span className={statValueClassName}>
                    {formatPrice(price_2.unitAmount, price_2.currency)}
                  </span>
                  <span className="text-muted-foreground ml-1">
                    {intervalLabel(price_2.recurringInterval, price_2.recurringIntervalCount)}
                  </span>
                </p>

                {features.length > 0 ? <ul className="text-muted-foreground flex flex-col gap-1.5">
                    {features.map(feature => <li className="flex items-start gap-2" key={feature}>
                        <Check className="text-primary mt-0.5 size-3.5 shrink-0" />
                        <span>{feature}</span>
                      </li>)}
                  </ul> : null}

                {isPerProject ? <p className={metaTextClassName}>
                    O checkout será aberto para {billedProjectsLabel}.
                  </p> : null}
              </CardContent>
              <CardFooter>
                <Button className="w-full" disabled={pendingAction !== null} onClick={() => isFreePlan ? void activateFreePlan(price_2._id) : void startCheckout(price_2._id)} size="sm">
                  {isPending ? isFreePlan ? "Ativando..." : "Abrindo checkout..." : isFreePlan ? "Ativar plano gratuito" : "Assinar este plano"}
                </Button>
              </CardFooter>
            </Card>;
    })}
      </div> : <div className="flex flex-col items-start gap-3">
        <Alert variant="warning">
          <AlertTitle>Planos indisponíveis</AlertTitle>
          <AlertDescription>
            Não encontramos planos públicos sincronizados agora. Tente iniciar o checkout padrão.
          </AlertDescription>
        </Alert>
        <Button disabled={pendingAction !== null} onClick={() => void startCheckout()} size="sm">
          {primaryPending ? "Abrindo checkout..." : "Assinar agora"}
        </Button>
      </div>;
  const actionButtons = <div className="flex flex-wrap gap-2">
      <Button disabled={pendingAction !== null} onClick={primaryAction} size="sm">
        {primaryPending ? primaryPendingLabel : primaryLabel}
      </Button>
      {!hasActiveSubscription && canOpenBillingPortal ? <Button disabled={pendingAction !== null} onClick={() => void startCheckout()} size="sm" variant="outline">
          {pendingAction === "checkout" ? "Abrindo checkout..." : "Retomar assinatura"}
        </Button> : null}
    </div>;
  const subscriptionSummary = <div className="flex flex-col gap-4">
      <StatGrid className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-2">
        <StatTile hint={activePlanInterval ?? undefined} label="Plano atual" value={activePlanName ?? "Plano vinculado ao workspace"} />
        <StatTile hint={activeAmountDisplay ? [activeAmountDisplay.secondary, activeAmountDisplay.detail].filter(Boolean).join(" · ") || undefined : plans === undefined ? "Carregando valores do plano..." : "Não foi possível carregar o valor deste plano no catálogo atual."} label="Valor da assinatura" value={activeAmountDisplay ? activeAmountDisplay.primary : plans === undefined ? <Skeleton className="h-6 w-28" /> : EMPTY_VALUE} />
      </StatGrid>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <DetailsItem label="Próxima renovação" value={periodEnd ?? "Não disponível"} />
        {periodStart ? <DetailsItem label="Início do período" value={periodStart} /> : null}
        {status === "trialing" && trialEnd ? <DetailsItem label="Fim do período de teste" value={trialEnd} /> : null}
        {billing?.cancelAtPeriodEnd ? <DetailsItem label="Cancelamento" value={`A assinatura será encerrada ao fim do período${periodEnd ? ` (${periodEnd})` : ""}.`} /> : null}
        {activePriceOption?.price.metadata?.billingModel === "per_project" ? <DetailsItem label="Projetos cobrados" value={`${billedProjectsLabel} ativo${billedProjectCount === 1 ? "" : "s"}`} /> : null}
      </div>
    </div>;
  const subscriptionFallback = <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <DetailsItem label="Status" value={statusLabels[status] ?? status} />
      <DetailsItem label="Próxima renovação" value={periodEnd ?? "Não disponível"} />
    </div>;
  return <SectionStack className={cn("mx-auto w-full", mode === "settings" ? "max-w-none" : "max-w-3xl")}>
      {isRequiredPlanChooser ? <Section icon={CreditCard} title="Escolha um plano">
          <p className="text-muted-foreground">
            Escolha um plano para ativar o acesso deste workspace.
          </p>
          {requiredPlanChooser}
        </Section> : <Section actions={<>
              {statusBadge}
              {activePlanBadge && showSubscriptionSummary ? <Badge variant="outline">{activePlanBadge}</Badge> : null}
            </>} icon={CreditCard} title="Assinatura atual">
          {showSubscriptionSummary ? subscriptionSummary : subscriptionFallback}
          {!shouldShowPlanSelector ? actionButtons : null}
        </Section>}

      {shouldShowPlanSelector ? <Section title="Plano">
          <p className="text-muted-foreground">Selecione o plano antes de iniciar o checkout.</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {priceOptions.map(({
          product: product_0,
          price: price_3
        }) => {
          const isSelected = checkoutPriceId === price_3._id;
          const isPerProject_0 = price_3.metadata?.billingModel === "per_project";
          const planName_0 = getPlanDisplayName({
            fallbackName: product_0.name,
            priceMetadata: price_3.metadata,
            productMetadata: product_0.metadata,
            recurringInterval: price_3.recurringInterval
          });
          return <button aria-label={`Selecionar ${planName_0} ${intervalLabel(price_3.recurringInterval, price_3.recurringIntervalCount)}`} aria-pressed={isSelected}
          // ui-audit-ignore-next-line layout/no-hand-rolled-panel: selectable plan tile (button)
          className="hover:border-primary data-[selected=true]:border-primary data-[selected=true]:bg-accent flex min-h-24 rounded-lg border p-3 text-left transition-colors" data-selected={isSelected} key={price_3._id} onClick={() => setSelectedPriceId(price_3._id)} type="button">
                  <span className="flex flex-1 flex-col gap-2">
                    <span className="flex items-start justify-between gap-2">
                      <span className="font-medium">{planName_0}</span>
                      {isSelected ? <Check className="text-primary size-3.5" /> : null}
                    </span>
                    <span>
                      <span className={statValueClassName}>
                        {formatPrice(price_3.unitAmount, price_3.currency)}
                      </span>
                      <span className="text-muted-foreground ml-1">
                        {intervalLabel(price_3.recurringInterval, price_3.recurringIntervalCount)}
                      </span>
                    </span>
                    {isPerProject_0 ? <span className={metaTextClassName}>
                        {billedProjectsLabel} cobrado{billedProjectCount === 1 ? "" : "s"} no
                        checkout
                      </span> : null}
                  </span>
                </button>;
        })}
          </div>
          {actionButtons}
        </Section> : null}
    </SectionStack>;
}